<template>
  <div class="validation" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1
        class="local-hero-header"
        v-translate="{progName: organizationDetails.programName}"
      >
        The %{progName}
      </h1>
    </div>

    <div class="form-container validation-form">
      <h1 v-translate>
        Verify your mobile phone number
      </h1>

      <!-- 400 error -->
      <template v-if="status === 400">
        <div class="error-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/error-icon.png" />
          </div>
          <div class="tooltip">
            <span class="tooltip-text" v-translate>
              Something went wrong, please try again later.
            </span>
          </div>
        </div>
      </template>

      <!-- 500 error -->
      <template v-if="status == 500">
        <div class="error-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/error-icon.png" />
          </div>
          <div class="tooltip">
            <span v-if="!codeError" class="tooltip-text" v-translate>
              The code you entered did not match. Please verify that you entered the correct mobile phone number and try again later.
            </span>
            <span v-else class="tooltip-text">
              {{ displayErrorMessage }}
            </span>
          </div>
        </div>
      </template>

      <div class="form-field card-shape">
        <!-- 200 (OK) -->
        <template v-if="status == 200">
          <div class="form-header">
            <translate>We texted you a code.</translate>&nbsp;<translate>Please enter it below:</translate></span>
          </div>
        </template>

        <FormTextField
          v-if="status !== 500"
          id="validated-code"
          label="Code"
          :tabIndex=1
          :value.sync="validatedCode"
          :showErrorMessage="status == 422"
          errorMessage="The code you entered did not match."
          required
        />
        <div class="button-container">
          <button
            v-if="status !== 500"
            class="form-button"
            @click="verifyNumber"
          >
            <LoadSpinner v-if="verifying" :color="white" />
            <span v-else v-translate>Verify</span>
          </button>
          <button v-else class="form-button" @click="nextPage" v-translate>Continue</button>
        </div>
      </div>

      <div class="form-footer">
        <p v-translate>Didn't get our text?</p>
        <a @click="sendCode" v-translate>Resend code</a>
        <router-link :to="{ name: 'MyProfile' }" v-translate>
          Edit your mobile phone number
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import User from '@/services/users'
import Employee from '@/services/employees'
import LoadSpinner from '@/components/LoadSpinner'
import FormTextField from '@/components/forms/FormTextField'

export default {
  props: {
    codeError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
  },
  components: {
    LoadSpinner,
    FormTextField,
  },
  computed: {
    ...mapState(['organizationDetails']),
    displayErrorMessage() {
      // errorMessage normally is translated by the parent and passed in as a
      // prop: otherwise, display this default translated message
      const defaultMsg = this.$gettext('You have reached the code request limit. Please try again later.')

      return this.errorMessage || defaultMsg
    },
  },
  data() {
    return {
      status: 200,
      loading: false,
      user: null,
      validatedCode: '',
      verifying: false,
      count: 0,
      verified: false,
    }
  },
  async created() {
    if (this.codeError) {
      this.status = 500
    }
    this.loading = true
    this.userId = this.$store.state.user.id
    await Employee.api.retrieve(this.userId).then(res => {
      this.user = res
    })
    this.loading = false
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.user.validatedNumber && !this.verified) {
      await this.updateSmsForFailedVerification()
    }
    next()
  },
  methods: {
    nextPage() {
      /* to nav to the next page, include a route param as "routeName", we'll default this to Home though */
      const nextRoute = this.$route.params && this.$route.params['routeName'] || "Home"
      this.$router.push({ name: nextRoute, params: { code: this.$route.params.code }, })
    },
    async verifyNumber() {
      this.verifying = true
      try {
        await User.api.twilioVerify({ employee: this.user, code: this.validatedCode })
        this.verified = true
        this.nextPage()
      } catch (err) {
        if (err.response) {
          this.status = err.response.status
          this.verifying = false
        }
      }
      this.validatedCode = ''
      this.verifying = false
    },
    async updateSmsForFailedVerification() {
      this.user.enableSmsAlerts = false
      this.user.enableEmailAlerts = true
      try {
        const response = await Employee.api.update(this.user, [
          'enableSmsAlerts',
          'enableEmailAlerts',
        ])
        this.user = response
        this.$store.dispatch('updateUser', Employee.fromAPI(response))
        this.$store.commit('UPDATE_CURRENT_LANGUAGE', this.user.preferredLanguage)
      } catch {
        this.user.id = this.userId
      }
    },
    async sendCode() {
      try {
        await User.api.twilioChallenge(this.user)
        this.validatedCode = ''
      } catch (err) {
        if (err.response) {
          this.status = err.response.status
          this.codeError = true
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.validation {
  display: flex;
  flex-direction: column;

  .validation-form {
    @extend .constrain-tablet-and-up;

    h1 {
      margin-bottom: 2.5rem;
    }
  }

  .card-shape, .form-footer, .error-container {
    width: 100%;
    align-self: center;
    max-width: 500px;
  }

  .form-footer {
    display: flex;
    flex-direction: column;

    p {
      font-weight: bold;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__text {
    text-align: center;
  }

  &__footer {
    margin-top: 1em;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__link {
      display: block;
      margin-top: 0.25em;
      cursor: pointer;
    }
  }
}

</style>
